import i18n, { loadLanguageAsync } from '@/utils/i18n'
import EventBus from '@/utils/eventBus'
import router from '@/router'
import store from '@/store'
import { getUserTenantPerms } from '@/api/users/request'
import { aclHelper } from '@/utils/aclHelper'
import { noExpireCheckPages, workPermitRouteNames, tenderRouteNames, projectRouteNames, performanceRouteNames, eCircularRouteNames, directoryRouteNames, tenancyRecordsRouteNames, tenantProjectRouteNames, FEATURE_NAMES, envParameterNames } from '@/utils/consts'
import { ENGINEER_DASHBOARD_ROUTE } from '@/utils/locale'
import { canAccessRouteByUserType } from './access'
import { isProjectControlLeaveRoute, shouldUseProjectControlLeaveModal } from '@/modules/tenantProjects/projectControl/leaveModal.consts'

const preCheckEngineerAccessRoutes = ['tenders', 'projects', 'home', ENGINEER_DASHBOARD_ROUTE.TMS, 'performance', 'contacts', 'ccdContactCreate', 'ccdTenancyCreate']
const preCheckContractorAccessRoutes = ['vendorHome', 'vendorContacts']
const skipCheckModuleCfgRoutes = ['notFound', 'login', 'adLogin', 'contractorTermsOfUse', 'tenantTermsOfUse', 'logout']
const skipDirtyCheckRoutes = ['authenticationCode']

export const loadSystemSettings = async (to, from, next) => {
  const lang = store.getters['language/currentLanguage']
  await loadLanguageAsync(lang)
  const backendEnv = [
    'ext.AD.mock',
    'security.login.force2FA',
    'security.login.authenticationCodeExpireSec',
    envParameterNames.vendorEntry,
    envParameterNames.engineerEntry,
  ]
  await store.dispatch('settings/loadBackEndEnv', backendEnv)
  return next()
}

export const checkFeatureStatus = (to, from, next) => {
  const isLogin = store.getters['user/isLogin']
  // if the user didn't login, then skip the checker
  if (skipCheckModuleCfgRoutes.includes(to.name) || !isLogin) {
    next()
  } else {
    const featureStatus = store.getters['regions/featureStatus']
    const needCheckRouteMap = {
      [FEATURE_NAMES.TENDERS]: tenderRouteNames,
      [FEATURE_NAMES.HONGKONG_LAND_PROJECTS]: projectRouteNames,
      [FEATURE_NAMES.VENDOR_PERFORMANCE]: performanceRouteNames,
      [FEATURE_NAMES.E_CIRCULAR]: eCircularRouteNames,
      [FEATURE_NAMES.DIRECTORY]: directoryRouteNames,
      [FEATURE_NAMES.TENANCY_RECORDS]: tenancyRecordsRouteNames,
      [FEATURE_NAMES.TENANT_PROJECTS]: tenantProjectRouteNames,
    }
    for (const key of Object.keys(FEATURE_NAMES)) {
      if (!featureStatus[key] && needCheckRouteMap[key] && Object.keys(needCheckRouteMap[key]).includes(to.name)) {
        return next('/404')
      }
    }
    next()
  }
}

export const handleHomeLogin = async (to, from, next) => {
  const isContractor = store.getters['user/isContractor']
  const isTenantUser = store.getters['user/isTenant']
  const currentUser = store.getters['user/currentUser']
  const isCurrentUserExists = Object.values(currentUser).length > 0
  const isVendorPath = to.path.startsWith('/vendors')
  const isTenantPath = to.path.startsWith('/tenants')
  const isAdMock = store.getters['settings/isAdMock']
  if (!isCurrentUserExists && !noExpireCheckPages.includes(to.name)) {
    // if a user type the url without any pattern (only hostname), will redirect user to external login
    const isRootPath = to.path === '/'
    const engineerLogin = isAdMock ? 'login' : 'adLogin'
    let loginParams = { name: isVendorPath || isTenantPath || isRootPath ? 'publicLogin' : engineerLogin }

    const redirectUrl = to.fullPath
    if (redirectUrl && redirectUrl !== '/404') {
      loginParams.params = { redirect: redirectUrl }
      if (loginParams.name === from.name) {
        router.currentRoute.params.redirect = redirectUrl
        return next(false)
      }
    }
    return next(loginParams)
  }
  if (isCurrentUserExists && to.path === '/') {
    if (isContractor) return next({ name: 'vendorHome' })
    if (isTenantUser) return next({ name: 'tenantDashboard' })
  }
  if ((preCheckEngineerAccessRoutes.includes(to.name) && isContractor) ||
    (preCheckContractorAccessRoutes.includes(to.name) && !isContractor)) {
    EventBus.emit('modules_no_perm_modal_open')
    return next(false)
  }
  const isEngineer = store.getters['user/isEngineer']
  if (isEngineer && isTenantPath && isConvertibleRoute(to.name)) {
    const redirectTo = {
      name: convertPublicRouteToHKL(to.name),
      params: to.params,
      query: to.query,
    }
    return next(redirectTo)
  }

  if (to.hash.startsWith('#id_token')) {
    const loginParams = {name: 'adLogin'}
    return next(loginParams)
  }

  next()
}

export const checkUserType = (to, from, next) => {
  if (!canAccessRouteByUserType(to)) {
    EventBus.emit('modules_no_perm_modal_open')
    return next(false)
  } else {
    return next()
  }
}

export const checkPermission = async (to, from, next) => {
  const isContractor = store.getters['user/isContractor']
  for (let i = 0; i < to.matched.length; i++) {
    let routeRecord = to.matched[i]
    if (to.matched.length > 1 && routeRecord.parent === undefined && !routeRecord.meta.acl) {
      continue
    }
    if (store.state.user.currentUser && routeRecord.meta.acl) {
      const permTypes = routeRecord.meta.acl.permTypes
      const fallback = routeRecord.meta.acl.fallback
      const contractorView = routeRecord.meta.acl.contractorView
      const validUserToAccess = (isContractor && contractorView) || !(isContractor || contractorView)
      if (permTypes && permTypes.length > 0) {
        const allowed = permTypes.some((permType) => {
          const granted = aclHelper.getAccess(permType)
          return granted
        })

        const fallbackTarget = validUserToAccess && fallback ? { name: fallback, params: to.params } : false
        const nextTarget = allowed ? true : fallbackTarget

        if (!nextTarget) {
          EventBus.emit('modules_no_perm_modal_open')
        }
        next(nextTarget)
        break
      }
    }
    next()
    break
  }
}

export const checkTenancyRecordAccess = async (to, from, next) => {
  if (to.name !== tenancyRecordsRouteNames.tenantAllTenancyRecs) return next()

  const currentUser = store.getters['user/currentUser']
  const user = await getUserTenantPerms(currentUser.id).catch(() => ({}))
  if ((user.tenancyAccount || []).length > 0) return next()

  EventBus.emit('modules_no_perm_modal_open')
  return next({ name: 'tenantDashboard' })
}

export const checkFormDirty = (to, from, next) => {
  if (skipDirtyCheckRoutes.includes(from.name)) {
    return next(true)
  }
  const shouldBlockRouteChange = store.getters['form/isPageDirty']
  if (shouldBlockRouteChange) {
    if (isProjectControlLeaveRoute(from.name) &&
      shouldUseProjectControlLeaveModal(store.getters['form/dirtyComponents'] || {})) {
      EventBus.emit('modules_project_control_leave_do_nav', { to })
    } else {
      EventBus.emit('modules_confirm_continue_do_nav', { to })
    }
  }
  next(!shouldBlockRouteChange)
}

export const workPermitBatchApproveCheck = (to, from, next) => {
  const isUnderBatch = store.getters['workPermit/isUnderBatch']
  const shouldBlockRouteChange = !to.query.underBatch && from.query.underBatch && isUnderBatch

  if (shouldBlockRouteChange) {
    EventBus.emit('modules_confirm_continue_do_nav', {to, message: i18n.t('workPermit.leaveBatchWarn')})
  }
  next(!shouldBlockRouteChange)
}

/**
 * Vue Router 2 cancels in-flight navigations without running afterEach, but prePageLoaded
 * may already have run for the aborted attempt — peel one stop before the next start.
 */
export const compensateAbortedPageLoading = (to, from, next) => {
  if (from && (from.name || from.path)) {
    EventBus.emit('module_loading_stopLoading')
  }
  next()
}

export const prePageLoaded = (to, from, next) => {
  EventBus.emit('module_loading_startLoading')
  next()
}

export const postPageLoaded = (to) => {
  EventBus.emit('module_loading_stopLoading')
  EventBus.emit('module_vue_router_finished_loading', to)
}

export const setupPage = (to, from) => {
  const tenderpages = ['tenders', 'tenderSummary', 'analysisRecos', 'healthCheck', 'addendumSummary', 'tenderDetails', 'tenderDetailsView', 'tenderAddendumDetailsView', 'tenderAddendumDetails', 'tenderComparison', 'tmView']
  if (tenderpages.includes(to.name) && from.name === 'tenders') {
    to.params.prev = 'viewAll'
  }
  EventBus.emit('apps_stop_check_expire')
  if (noExpireCheckPages.indexOf(to.name) < 0 &&
    store.getters['user/currentUser']) {
    EventBus.emit('apps_start_check_expire')
  }
}

const convertibleRouteMap = {
  tenantTenancyECirculars: 'ccdTenancyECirculars',
  ccdTenancyTenantView: 'ccdTenancyView',
  tenantCcdContactView: 'ccdContactView',
  tenantHandover: 'handover',
  tenantProjectDetail: 'tenantProject',
  tenantDirectories: 'tenancyDirectories',
  tenantDirectoryDetail: 'directoryDetail',
}

export function isConvertibleRoute (name) {
  return !!convertibleRouteMap[name]
}

export function convertPublicRouteToHKL (name) {
  return convertibleRouteMap[name]
}

export function checkIfHKLProjectRelatedRoute () {
  const shouldCheckRoutes = [projectRouteNames.projectDetail, projectRouteNames.vendorProjectDetail, workPermitRouteNames.vendorCreateWorkPermit]
  return shouldCheckRoutes.includes(router.currentRoute.name)
}

const afterLoginFn = (callback, fallback = () => undefined) => {
  return async (...args) => {
    const [to, from] = args
    const afterAuthPageList = ['authenticationCode', 'login', 'adLogin', 'publicLogin']
    if (afterAuthPageList.includes(from.name) && !noExpireCheckPages.includes(to.name)) {
      return typeof callback === 'function' && callback.apply(undefined, args)
    } else {
      return typeof fallback === 'function' && fallback.apply(undefined, args)
    }
  }
}

export const getAllBuildingsAfterLogin = afterLoginFn(async () => {
  await store.dispatch('buildings/getAllBuildings')
})

export const getCurrenciesAfterLogin = afterLoginFn(async () => {
  await store.dispatch('currencies/loadPurchaseCurrencies')
})
